// src/pages/reports/DailyOrdersVsReturnsSummary.tsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ChevronDown, Calendar, Upload } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI, branchesAPI, returnsAPI, departmentAPI } from '../services/api';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  format,
  isWithinInterval,
  parseISO,
  isSameDay,
} from 'date-fns';
import { arSA } from 'date-fns/locale';

interface OrderRow {
  id: string;
  product: string;
  unit: string;
  department: string;
  departmentId: string;
  branchQuantities: { [branch: string]: { orders: number; returns: number } };
  totalOrders: number;
  totalReturns: number;
}

interface Branch {
  _id: string;
  name: string;
  nameEn: string;
  displayName: string;
}

interface Department {
  _id: string;
  name: string;
  nameEn?: string;
}

const PERIOD_OPTIONS = [
  { value: 'today', labelAr: 'اليوم', labelEn: 'Today' },
  { value: 'week', labelAr: 'آخر ٧ أيام', labelEn: 'Last 7 Days' },
  { value: 'month', labelAr: 'آخر ٣٠ يومًا', labelEn: 'Last 30 Days' },
  { value: 'custom', labelAr: 'فترة مخصصة', labelEn: 'Custom Range' },
];

const toArabicNumerals = (number: string | number): string => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return String(number).replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
};

const formatNumber = (num: number, isRtl: boolean): string => {
  return isRtl ? toArabicNumerals(num.toString()) : num.toString();
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
};

const loadFont = async (doc: jsPDF): Promise<boolean> => {
  const fontName = 'Amiri';
  const fontUrls = {
    regular: 'https://raw.githubusercontent.com/aliftype/amiri/master/fonts/Amiri-Regular.ttf',
    bold: 'https://raw.githubusercontent.com/aliftype/amiri/master/fonts/Amiri-Bold.ttf',
  };
  try {
    const [regularRes, boldRes] = await Promise.all([
      fetch(fontUrls.regular),
      fetch(fontUrls.bold),
    ]);
    const [regularFontBytes, boldFontBytes] = await Promise.all([
      regularRes.arrayBuffer(),
      boldRes.arrayBuffer(),
    ]);
    doc.addFileToVFS(`${fontName}-normal.ttf`, arrayBufferToBase64(regularFontBytes));
    doc.addFont(`${fontName}-normal.ttf`, fontName, 'normal');
    doc.addFileToVFS(`${fontName}-bold.ttf`, arrayBufferToBase64(boldFontBytes));
    doc.addFont(`${fontName}-bold.ttf`, fontName, 'bold');
    return true;
  } catch (error) {
    console.error('Font loading error:', error);
    return false;
  }
};

const generateFileName = (prefix: string, periodLabel: string, isRtl: boolean, extension: string): string => {
  const dateStr = new Date().toISOString().split('T')[0];
  const sanitizedLabel = periodLabel.replace(/[^a-zA-Z0-9\u0600-\u06FF]/g, '_');
  return `${prefix}_${sanitizedLabel}_${dateStr}.${extension}`;
};

const generatePDFHeader = (
  doc: jsPDF,
  isRtl: boolean,
  periodLabel: string,
  fontName: string,
  fontLoaded: boolean
) => {
  const pageWidth = doc.internal.pageSize.width;
  doc.setFont(fontLoaded ? fontName : 'helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(40, 40, 40);
  const mainTitle = isRtl ? `مقارنة الطلبات والمرتجعات` : `Orders vs Returns Summary`;
  doc.text(mainTitle, pageWidth / 2, 25, { align: 'center' });
  doc.setFontSize(12);
  doc.setTextColor(80, 80, 80);
  doc.text(periodLabel, pageWidth / 2, 35, { align: 'center' });
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.8);
  doc.line(20, 45, pageWidth - 20, 45);
};

const generatePDFTable = (
  doc: jsPDF,
  headers: string[],
  data: any[][],
  isRtl: boolean,
  fontLoaded: boolean,
  fontName: string
) => {
  autoTable(doc, {
    head: [isRtl ? headers.slice().reverse() : headers],
    body: isRtl ? data.map(row => row.slice().reverse()) : data,
    startY: 52,
    theme: 'grid',
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [50, 50, 50],
      fontSize: 10,
      halign: 'center',
      font: fontLoaded ? fontName : 'helvetica',
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 9,
      halign: 'center',
      font: fontLoaded ? fontName : 'helvetica',
    },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    didParseCell: (hookData) => {
      if (hookData.cell.text) {
        hookData.cell.text = hookData.cell.text.map(text => {
          if (typeof text === 'string' && /[0-9]/.test(text)) {
            return text.replace(/[0-9]/g, d => toArabicNumerals(d));
          }
          return text;
        });
      }
      if (hookData.section === 'body' && hookData.row.index === data.length - 1) {
        hookData.cell.styles.fontStyle = 'bold';
        hookData.cell.styles.fillColor = [240, 240, 240];
      }
    },
  });
};

const exportToPDF = async (
  data: any[][],
  periodLabel: string,
  headers: string[],
  isRtl: boolean
) => {
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف PDF...' : 'Generating PDF...', { toastId: 'pdf-export' });
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const fontName = 'Amiri';
    const fontLoaded = await loadFont(doc);
    generatePDFHeader(doc, isRtl, periodLabel, fontName, fontLoaded);
    generatePDFTable(doc, headers, data, isRtl, fontLoaded, fontName);
    const fileName = generateFileName('OrdersVsReturns', periodLabel, isRtl, 'pdf');
    doc.save(fileName);
    toast.update('pdf-export', { render: isRtl ? 'تم التصدير بنجاح' : 'PDF exported', type: 'success', autoClose: 3000 });
  } catch (error) {
    toast.update('pdf-export', { render: isRtl ? 'فشل في التصدير' : 'Export failed', type: 'error', autoClose: 3000 });
  }
};

// Components
const Button: React.FC<{ variant: 'primary' | 'secondary'; onClick?: () => void; disabled?: boolean; children: React.ReactNode }> = ({
  variant, onClick, disabled, children
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
      variant === 'primary' && !disabled
        ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-md'
        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
    }`}
  >
    {children}
  </button>
);

const ProductSearchInput: React.FC<{ value: string; onChange: any; placeholder: string; ariaLabel: string }> = ({
  value, onChange, placeholder, ariaLabel
}) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  return (
    <div className="relative group w-full">
      <motion.div
        className={`absolute inset-y-0 ${isRtl ? 'left-3' : 'right-3'} flex items-center text-gray-400 group-focus-within:text-amber-500`}
        animate={{ opacity: value ? 0 : 1 }}
      >
        <Search className="w-4 h-4" />
      </motion.div>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${isRtl ? 'pr-3 pl-4' : 'pl-3 pr-4'} py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 text-sm ${isRtl ? 'text-right' : 'text-left'}`}
        aria-label={ariaLabel}
      />
      {value && (
        <button
          onClick={() => onChange({ target: { value: '' } })}
          className={`absolute inset-y-0 ${isRtl ? 'left-3' : 'right-3'} flex items-center text-gray-400 hover:text-amber-600`}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const ProductDropdown: React.FC<{ value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; ariaLabel: string }> = ({
  value, onChange, options, ariaLabel
}) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value)?.label || options[0]?.label;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2 border rounded-lg flex justify-between items-center text-sm bg-white shadow-sm"
      >
        <span className="truncate">{selected}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {options.map(opt => (
              <button
                dir='rtl'
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className="w-full px-4 py-2 text-sm hover:bg-amber-50 text-left"
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DailyOrdersVsReturnsSummary: React.FC = () => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const { user } = useAuth();

  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [data, setData] = useState<OrderRow[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const periodOptions = PERIOD_OPTIONS.map(p => ({ value: p.value, label: isRtl ? p.labelAr : p.labelEn }));

  const getDateRange = useCallback(() => {
    const now = new Date();
    let start: Date, end: Date, label: string;

    switch (selectedPeriod) {
      case 'today':
        start = startOfDay(now);
        end = endOfDay(now);
        label = isRtl ? `اليوم - ${format(start, 'EEEE d/M/yyyy', { locale: arSA })}` : `Today - ${format(start, 'EEEE d/M/yyyy')}`;
        break;
      case 'week':
        start = subDays(now, 6);
        end = endOfDay(now);
        label = isRtl ? 'آخر ٧ أيام' : 'Last 7 Days';
        break;
      case 'month':
        start = subMonths(now, 1);
        end = endOfDay(now);
        label = isRtl ? 'آخر ٣٠ يومًا' : 'Last 30 Days';
        break;
      case 'custom':
        if (!customStartDate || !customEndDate) return null;
        start = startOfDay(parseISO(customStartDate));
        end = endOfDay(parseISO(customEndDate));
        if (start > end) return null;
        label = isRtl
          ? `${format(start, 'd/M/yyyy', { locale: arSA })} - ${format(end, 'd/M/yyyy', { locale: arSA })}`
          : `${format(start, 'd/M/yyyy')} - ${format(end, 'd/M/yyyy')}`;
        break;
      default:
        return null;
    }
    return { start: start.toISOString(), end: end.toISOString(), label };
  }, [selectedPeriod, customStartDate, customEndDate, isRtl]);

  const allBranches = useMemo(() => branches.map(b => b.displayName).sort((a, b) => a.localeCompare(b, language)), [branches, language]);

  const fetchData = useCallback(async () => {
    if (!user || !['admin', 'production'].includes(user.role)) return setLoading(false);

    const range = getDateRange();
    if (!range) return setLoading(false);

    setLoading(true);
    try {
      const [branchesRes, ordersRes, returnsRes, deptsRes] = await Promise.all([
        branchesAPI.getAll(),
        ordersAPI.getAll({ startDate: range.start, endDate: range.end, limit: 10000 }),
        returnsAPI.getAll({ startDate: range.start, endDate: range.end, limit: 10000 }),
        departmentAPI.getAll({ limit: 100 })
      ]);

      const fetchedBranches: Branch[] = (Array.isArray(branchesRes) ? branchesRes : []).map((b: any) => ({
        _id: b._id,
        name: b.name || '',
        nameEn: b.nameEn || b.name,
        displayName: isRtl ? b.name : (b.nameEn || b.name),
      })).sort((a, b) => a.displayName.localeCompare(b.displayName));

      setBranches(fetchedBranches);
      setDepartments(Array.isArray(deptsRes) ? deptsRes : []);

      const branchMap = new Map(fetchedBranches.map(b => [b._id, b.displayName]));
      const productMap = new Map<string, { name: string; unit: string; dept: string; deptId: string }>();

      const orders = Array.isArray(ordersRes) ? ordersRes.filter((o: any) => o.status !== 'cancelled') : [];
      const returns = Array.isArray(returnsRes.returns || returnsRes) ? (returnsRes.returns || returnsRes).filter((r: any) => r.status === 'approved') : [];

      const rowMap = new Map<string, OrderRow>();

      // Process Orders
      orders.forEach((order: any) => {
        const date = parseISO(order.createdAt || order.date);
        if (!isWithinInterval(date, { start: parseISO(range.start), end: parseISO(range.end) })) return;

        const branchName = branchMap.get(order.branch?._id || order.branchId) || (isRtl ? 'الفرع الرئيسي' : 'Main Branch');

        (order.items || []).forEach((item: any) => {
          if (item.rejected || item.rejectedByBranch) return;
          const pid = item.product?._id || item.productId;
          if (!pid) return;

          const pName = isRtl ? (item.product?.name || 'غير معروف') : (item.product?.nameEn || item.product?.name || 'Unknown');
          const unit = isRtl ? (item.product?.unit || 'وحدة') : (item.product?.unitEn || item.product?.unit || 'Unit');
          const dept = item.product?.department;
          const deptName = isRtl ? (dept?.name || 'عام') : (dept?.nameEn || dept?.name || 'General');
          const deptId = dept?._id || 'general';

          if (!productMap.has(pid)) {
            productMap.set(pid, { name: pName, unit, dept: deptName, deptId });
          }

          const key = pid;
          if (!rowMap.has(key)) {
            rowMap.set(key, {
              id: key,
              product: pName,
              unit,
              department: deptName,
              departmentId: deptId,
              branchQuantities: {},
              totalOrders: 0,
              totalReturns: 0,
            });
          }

          const row = rowMap.get(key)!;
          if (!row.branchQuantities[branchName]) {
            row.branchQuantities[branchName] = { orders: 0, returns: 0 };
          }
          row.branchQuantities[branchName].orders += Number(item.quantity) || 0;
          row.totalOrders += Number(item.quantity) || 0;
        });
      });

      // Process Returns
      returns.forEach((ret: any) => {
        const date = parseISO(ret.createdAt);
        if (!isWithinInterval(date, { start: parseISO(range.start), end: parseISO(range.end) })) return;

        const branchName = branchMap.get(ret.branch?._id || ret.branchId) || (isRtl ? 'الفرع الرئيسي' : 'Main Branch');

        (ret.items || []).forEach((item: any) => {
          const pid = item.product?._id || item.product;
          if (!pid) return;

          const key = pid;
          if (!rowMap.has(key)) {
            const details = productMap.get(pid) || { name: 'غير معروف', unit: 'وحدة', dept: 'عام', deptId: 'general' };
            rowMap.set(key, {
              id: key,
              product: details.name,
              unit: details.unit,
              department: details.dept,
              departmentId: details.deptId,
              branchQuantities: {},
              totalOrders: 0,
              totalReturns: 0,
            });
          }

          const row = rowMap.get(key)!;
          if (!row.branchQuantities[branchName]) {
            row.branchQuantities[branchName] = { orders: 0, returns: 0 };
          }
          row.branchQuantities[branchName].returns += Number(item.quantity) || 0;
          row.totalReturns += Number(item.quantity) || 0;
        });
      });

      setData(Array.from(rowMap.values()).sort((a, b) => (b.totalOrders + b.totalReturns) - (a.totalOrders + a.totalReturns)));
    } catch (err) {
      toast.error(isRtl ? 'فشل في تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user, getDateRange, isRtl, language]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const departmentOptions = useMemo(() => [
    { value: 'all', label: isRtl ? 'جميع الأقسام' : 'All Departments' },
    ...departments.map(d => ({ value: d._id, label: isRtl ? d.name : (d.nameEn || d.name) }))
  ], [departments, isRtl]);

  const filteredData = useMemo(() => data.filter(row =>
    row.product.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (selectedDepartment === 'all' || row.departmentId === selectedDepartment)
  ), [data, searchTerm, selectedDepartment]);

  const periodLabel = getDateRange()?.label || '';

  const exportTable = () => {
    const headers = [
      isRtl ? 'المنتج' : 'Product',
      ...allBranches.flatMap(b => [b + (isRtl ? ' (طلبات)' : ' (Orders)'), b + (isRtl ? ' (مرتجعات)' : ' (Returns)')]),
      isRtl ? 'إجمالي الطلبات' : 'Total Orders',
      isRtl ? 'إجمالي المرتجعات' : 'Total Returns',
      isRtl ? 'وحدة' : 'Unit',
    ];

    const rows = filteredData.map(row => [
      row.product,
      ...allBranches.flatMap(branch => [
        formatNumber(row.branchQuantities[branch]?.orders || 0, isRtl),
        formatNumber(row.branchQuantities[branch]?.returns || 0, isRtl),
      ]),
      formatNumber(row.totalOrders, isRtl),
      formatNumber(row.totalReturns, isRtl),
      row.unit,
    ]);

    rows.push([
      isRtl ? 'الإجمالي' : 'Total',
      ...allBranches.flatMap(branch => [
        formatNumber(filteredData.reduce((s, r) => s + (r.branchQuantities[branch]?.orders || 0), 0), isRtl),
        formatNumber(filteredData.reduce((s, r) => s + (r.branchQuantities[branch]?.returns || 0), 0), isRtl),
      ]),
      formatNumber(filteredData.reduce((s, r) => s + r.totalOrders, 0), isRtl),
      formatNumber(filteredData.reduce((s, r) => s + r.totalReturns, 0), isRtl),
      '',
    ]);

    exportToPDF(rows, periodLabel, headers, isRtl);
  };

  if (!user || !['admin', 'production'].includes(user.role)) {
    return <div className="text-center py-12">{isRtl ? 'لا توجد صلاحية' : 'No access'}</div>;
  }

  return (
    <div className={`min-h-screen px-4 py-6 ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="mb-6 bg-white shadow-lg rounded-xl p-6 border">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-4 mb-4">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-3">
            <Calendar className="w-6 h-6 text-amber-600" />
            {isRtl ? `مقارنة الطلبات والمرتجعات - ${periodLabel}` : `Orders vs Returns - ${periodLabel}`}
          </h2>
          
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3"
        >
           <div className="sm:col-span-2">
          <ProductSearchInput value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={isRtl ? 'ابحث عن منتج...' : 'Search product...'} ariaLabel="بحث" />
      </div>
          <div className="sm:col-span-1">
          <ProductDropdown value={selectedPeriod} onChange={setSelectedPeriod} options={periodOptions} ariaLabel="فترة زمنية" />
          {selectedPeriod === 'custom' && (
            <>
              <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} className="px-4 py-2 border rounded-lg" />
              <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} className="px-4 py-2 border rounded-lg" />
            </>
          )}
          </div>
         
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-amber-600"></div>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow">لا توجد بيانات</div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="overflow-x-auto bg-white rounded-xl shadow-lg border">
          <table className="min-w-full text-xs">
            <thead className="bg-amber-50 sticky top-0">
              <tr>
                <th rowSpan={2} className="px-4 py-3 font-bold text-center min-w-48">المنتج</th>
                {allBranches.map(branch => (
                  <th key={branch} colSpan={2} className="px-4 py-2 font-bold text-center border-x">
                    {branch}
                  </th>
                ))}
                <th rowSpan={2} className="px-4 py-3 font-bold">إجمالي الطلبات</th>
                <th rowSpan={2} className="px-4 py-3 font-bold">إجمالي المرتجعات</th>
                <th rowSpan={2} className="px-4 py-3 font-bold">وحدة</th>
              </tr>
              <tr>
                {allBranches.flatMap(branch => [
                  <th key={`${branch}-orders`} className="px-2 py-2 bg-green-50 text-green-700 font-semibold">طلبات</th>,
                  <th key={`${branch}-returns`} className="px-2 py-2 bg-red-50 text-red-700 font-semibold">مرتجعات</th>,
                ])}
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredData.map(row => (
                <tr key={row.id} className="hover:bg-amber-50">
                  <td className="px-4 py-3 text-center font-medium">{row.product}</td>
                  {allBranches.map(branch => (
                    <>
                      <td className={`px-2 py-3 text-center ${row.branchQuantities[branch]?.orders > 0 ? 'bg-green-50 text-green-700' : ''}`}>
                        {formatNumber(row.branchQuantities[branch]?.orders || 0, isRtl)}
                      </td>
                      <td className={`px-2 py-3 text-center ${row.branchQuantities[branch]?.returns > 0 ? 'bg-red-50 text-red-700' : ''}`}>
                        {formatNumber(row.branchQuantities[branch]?.returns || 0, isRtl)}
                      </td>
                    </>
                  ))}
                  <td className="px-4 py-3 text-center font-bold text-green-700">{formatNumber(row.totalOrders, isRtl)}</td>
                  <td className="px-4 py-3 text-center font-bold text-red-700">{formatNumber(row.totalReturns, isRtl)}</td>
                  <td className="px-4 py-3 text-center">{row.unit}</td>
                </tr>
              ))}
              <tr className="bg-gray-100 font-bold">
                <td className="px-4 py-3 text-center">الإجمالي</td>
                {allBranches.map(branch => (
                  <>
                    <td className="px-2 py-3 text-center text-green-700">
                      {formatNumber(filteredData.reduce((s, r) => s + (r.branchQuantities[branch]?.orders || 0), 0), isRtl)}
                    </td>
                    <td className="px-2 py-3 text-center text-red-700">
                      {formatNumber(filteredData.reduce((s, r) => s + (r.branchQuantities[branch]?.returns || 0), 0), isRtl)}
                    </td>
                  </>
                ))}
                <td className="px-4 py-3 text-center text-green-700">
                  {formatNumber(filteredData.reduce((s, r) => s + r.totalOrders, 0), isRtl)}
                </td>
                <td className="px-4 py-3 text-center text-red-700">
                  {formatNumber(filteredData.reduce((s, r) => s + r.totalReturns, 0), isRtl)}
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </motion.div>
      )}
    </div>
  );
};

export default DailyOrdersVsReturnsSummary;